import {useEffect, useLayoutEffect, useState} from "react";
import {getLocalItemAsJsonFromKey, removeLocalItemFromKey, setLocalItemFromKey} from "../../services/LocalStorage";
import {kSystemKeys} from "../../constants/keys";
import {isArrayNullUndefinedOrEmpty, isNullOrUndefined} from "../../services/Validator";
import CoursesScreen from "../courses/CoursesScreen";
import {PageView} from "../../enums/PageView";
import {useResizeListener} from "../../listeners/SystemResizeListener";
import {recordPageViewMetric} from "../../services/AnalyticsRecorder";
import MetaFlashScreen from "./flash/MetaFlashScreen";
import MetaExerciserScreen from "./exerciser/MetaExerciserScreen";
import {CoursesMetaStep} from "../../enums/CoursesMetaStep";
import {useAppLayoutStore} from "../../stores/appLayoutStore";

export default function MetaCoursesScreen() {
    // Used in Router to manage Flash user flow

    // PROPERTIES
    // ---- Navigation
    const [coursesRenderKey, setCoursesRenderKey] = useState(0);
    const setHideSidebar = useAppLayoutStore((state) => state.setHideSidebar);
    const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false)
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false)
    const [coursesMetaStep, setCoursesMetaStep] = useState(CoursesMetaStep.COURSES)

    // ---- Flash
    const [selectedGrade, setSelectedGrade] = useState(null)
    const [flashQuestions, setFlashQuestions] = useState(null)
    const [questionIndex, setQuestionIndex] = useState(0)
    const [selectedOption, setSelectedOption] = useState(null)
    const [isFlashOver, setIsFlashOver] = useState(false)

    // ---- Exerciser
    const [levelAIdentifier, setLevelAIdentifier] = useState(null)
    const [exerciserProblem, setExerciserProblem] = useState(null)
    const [exerciserIndex, setExerciserIndex] = useState(0)
    const [isExerciserOver, setIsExerciserOver] = useState(false)

    // HOOKS
    // ---- Eventually close modal during screen resize
    useResizeListener(() => setIsLeaveModalOpen(false))

    // ---- At rendering : if local flash is in storage, set flash variables
    useEffect(() => {
        if (!isNullOrUndefined(localStorage.getItem(kSystemKeys.session))) {
            // Check either exerciser or flash
            const localSession = getLocalItemAsJsonFromKey(kSystemKeys.session);

            if (!isNullOrUndefined(localSession.metaStep) && localSession.metaStep === CoursesMetaStep.FLASH) {
                // Case : Flash was running
                if (!isNullOrUndefined(localSession.grade) && !isNullOrUndefined(localSession.questions) && !isNullOrUndefined(localSession.currentQuestion)) {
                    setFlashGrade(localSession.grade)
                    setFlashQuestions(localSession.questions)
                    setQuestionIndex(localSession.currentQuestion)
                    setIsFlashOver(localSession.isFlashOver)
                    setCoursesMetaStep(CoursesMetaStep.FLASH)
                    setIsSnackbarOpen(true)
                }
            }
            if (!isNullOrUndefined(localSession.metaStep) && localSession.metaStep === CoursesMetaStep.EXERCISER) {
                // Case : Exerciser was running
                if (!isNullOrUndefined(localSession.problem)
                    && !isNullOrUndefined(localSession.levelAIdentifier)
                    && !isNullOrUndefined(localSession.currentQuestion)
                    && !isNullOrUndefined(localSession.isExerciserOver)) {
                    setExerciserProblem(localSession.problem)
                    setExerciserIndex(localSession.currentQuestion)
                    setLevelAIdentifier(localSession.levelAIdentifier)
                    setIsExerciserOver(localSession.isExerciserOver)
                    setCoursesMetaStep(CoursesMetaStep.EXERCISER)
                    setIsSnackbarOpen(true)
                }
            }
        }
    }, []);

    // ---- Fires before repainting the screen : display or not app sidebar
    useLayoutEffect(() => {
        const isImmersive =
            coursesMetaStep === CoursesMetaStep.FLASH ||
            coursesMetaStep === CoursesMetaStep.EXERCISER;

        setHideSidebar(isImmersive);

        return () => setHideSidebar(false);
    }, [coursesMetaStep, setHideSidebar]);

    // BUILD
    switch (coursesMetaStep) {
        case CoursesMetaStep.COURSES:
            return <CoursesScreen key={coursesRenderKey}
                                  selectedGrade={selectedGrade}
                                  setFlashGrade={setFlashGrade}
                                  fetchQuestionsCallback={fetchFlashQuestionsCallback}
                                  fetchExerciserProblemCallback={fetchExerciserProblemCallback} />
        case CoursesMetaStep.FLASH:
            return <MetaFlashScreen selectedGrade={selectedGrade}
                                    questions={flashQuestions}
                                    questionIndex={questionIndex}
                                    selectedOption={selectedOption}
                                    setSelectedOption={setSelectedOption}
                                    submitAnswer={submitFlashQuestionAnswer}
                                    isLeaveModalOpen={isLeaveModalOpen}
                                    setIsLeaveModalOpen={setIsLeaveModalOpen}
                                    onLeaveFlashPressed={onLeavePressed}
                                    leaveSessionCallback={leaveSessionCallback}
                                    isSnackbarOpen={isSnackbarOpen}
                                    handleClose={handleClose}
                                    isFlashOver={isFlashOver} />
        case CoursesMetaStep.EXERCISER:
            return <MetaExerciserScreen isLeaveModalOpen={isLeaveModalOpen}
                                        setIsLeaveModalOpen={setIsLeaveModalOpen}
                                        onLeaveExerciserPressed={onLeavePressed}
                                        leaveSessionCallback={leaveSessionCallback}
                                        levelAIdentifier={levelAIdentifier}
                                        problem={exerciserProblem}
                                        currentIndex={exerciserIndex}
                                        setCurrentIndex={setExerciserIndex}
                                        isExerciserOver={isExerciserOver}
                                        setIsExerciserOver={setIsExerciserOver}
                                        isSnackbarOpen={isSnackbarOpen}
                                        handleClose={handleClose} />
        default:
            return <CoursesScreen selectedGrade={selectedGrade}
                                  setFlashGrade={setFlashGrade}
                                  fetchQuestionsCallback={fetchFlashQuestionsCallback} />
    }

    // METHODS
    // ---- ---- ---- ----
    // NAVIGATION
    // ---- ---- ---- ----
    function handleClose(event, reason) {
        if (reason === 'clickaway') {
            return;
        }
        setIsSnackbarOpen(false);
    }

    // ---- Leave flash or exerciser session
    function leaveSessionCallback() {
        // LOCAL : Clear local storage
        localStorage.clear()
        // NAV : Reset state
        setCoursesRenderKey((k) => k + 1);
        resetState()
    }

    // ---- On leave flash pressed
    function onLeavePressed() {
        // if (metaStep >= 1) {
        if (coursesMetaStep !== CoursesMetaStep.COURSES) {
            // Case : flash/exerciser screen => ask for confirmation
            setIsLeaveModalOpen(true)
        } else {
            // Case : level or rules step screen => leave flash/exerciser session
            leaveSessionCallback()
        }
    }

    // ---- ---- ---- ----
    // FLASH
    // ---- ---- ---- ----
    // ---- LOGIC : Set flash level in state and local
    function setFlashGrade(selectedGrade) {
        // STATE : Set selected level
        setSelectedGrade(selectedGrade)
        // LOCAL STORAGE : Get local flash, update and set local flash
        const localSession = getLocalItemAsJsonFromKey(kSystemKeys.session)
        setLocalItemFromKey(kSystemKeys.session, {...localSession, grade: selectedGrade})
    }

    // ---- CALLBACK : Flash questions callback (after api call)
    function fetchFlashQuestionsCallback(fetchedQuestions) {
        // Defensive measure
        if (isArrayNullUndefinedOrEmpty(fetchedQuestions))
            return
        // STATE : Set questions and flashMetaStep
        setFlashQuestions(fetchedQuestions)
        // const newFlashMetaStep = 1
        const newMetaStep = CoursesMetaStep.FLASH
        setCoursesMetaStep(newMetaStep)
        // LOCAL STORAGE : Clear existing, and set new
        removeLocalItemFromKey(kSystemKeys.session)
        const localSession = getLocalItemAsJsonFromKey(kSystemKeys.session)
        setLocalItemFromKey(kSystemKeys.session, {...localSession, metaStep: newMetaStep, questions: fetchedQuestions, currentQuestion: 0})
        // METRIC : Record launch flash metric
        recordPageViewMetric(PageView.FLASH)
    }

    // ---- LOGIC : Save answer & eventually go to correction
    function submitFlashQuestionAnswer() {
        // Get local session
        let localSession = getLocalItemAsJsonFromKey(kSystemKeys.session)
        // Destructuring question and adding selected answer
        flashQuestions[questionIndex] = {...flashQuestions[questionIndex], selectedOption: selectedOption}
        // Add selectedOption in localFlash
        localSession = {...localSession, questions: flashQuestions}
        // Emptying selected option var
        setSelectedOption(null)
        // Making flash go forward : next question or correction depending on index
        if (questionIndex === flashQuestions.length - 1) {
            // Case : last question
            // ---- Update state
            setIsFlashOver(true)
            // ---- Update local storage
            localSession = {...localSession, isFlashOver: true}
        } else {
            // Case : not last question
            setQuestionIndex(questionIndex + 1)
            localSession.currentQuestion = questionIndex + 1
        }
        // LOCAL STORAGE : Update local session
        setLocalItemFromKey(kSystemKeys.session, localSession)
    }

    // ---- ---- ---- ----
    // EXERCISER
    // ---- ---- ---- ----
    // ---- CALLBACK : Fetch exerciser problem (after api call)
    function fetchExerciserProblemCallback(levelAIdentifier, fetchedProblem) {
        // Defensive measure
        if (isNullOrUndefined(fetchedProblem))
            return
        // STATE : Set exerciser problem and meta step
        setLevelAIdentifier(levelAIdentifier)
        setExerciserProblem(fetchedProblem)
        const newMetaStep = CoursesMetaStep.EXERCISER
        setCoursesMetaStep(newMetaStep)
        // LOCAL STORAGE : Clear existing, and set new
        removeLocalItemFromKey(kSystemKeys.session)
        const localSession = getLocalItemAsJsonFromKey(kSystemKeys.session)
        setLocalItemFromKey(kSystemKeys.session, {...localSession, metaStep: newMetaStep, problem: fetchedProblem, levelAIdentifier: levelAIdentifier, currentQuestion: 0, isExerciserOver: false})
        // METRIC : To do
    }

    function resetState() {
        // ---- Navigation
        setIsLeaveModalOpen(false)
        setIsSnackbarOpen(false)
        setCoursesMetaStep(CoursesMetaStep.COURSES)
        // ---- Flash
        setSelectedGrade(null)
        setFlashQuestions(null)
        setQuestionIndex(0)
        setSelectedOption(null)
        setIsFlashOver(false)
        // ---- Exerciser
        setLevelAIdentifier(null)
        setExerciserProblem(null)
        setExerciserIndex(0)
        setIsExerciserOver(false)
    }

}